/**
 * Learn Mode — the overlay that runs on top of the real pages.
 *
 * Three pieces of chrome and nothing else:
 *   · a DEMO ribbon pinned to the top of the app frame, all session
 *   · a spotlight cut out of a dimming scrim, over a real control
 *   · a coach bar carrying one sentence and one thing to do
 *
 * THE SPOTLIGHT IS A GAP BETWEEN BLOCKERS. Four dimming panels are drawn around
 * the target's rect rather than one sheet over it, so the real control underneath
 * stays genuinely clickable — the agent taps the app, not a picture of it — while
 * everything else is inert. That second half matters as much as the first: it is
 * what stops an agent wandering mid-lesson into a photo upload or the Copilot
 * stream, both of which bypass axios entirely and so escape the demo sandbox.
 *
 * The bar flips above or below the target so it never covers the thing it is
 * describing, which is the failure mode a fixed-position coach bar always hits.
 *
 * ON DESKTOP the same three pieces are laid out for a window rather than a
 * 460px column: the ribbon spans the viewport, the bar is a fixed-width card
 * that tracks the anchor horizontally instead of filling the frame, and the
 * toast arrives at the top right where a desktop notification belongs.
 *
 * The blockers also change LAYER there, which is the one structural difference.
 * On the phone they sit above everything and the tour cuts a hole for the one
 * control a step wants — the app's own sheets are hand-rolled and the tour can
 * spotlight a row inside them. Desktop's overlays are AntD's: a Select dropdown,
 * a Drawer, a Modal, none of which will hand out an anchor per option. So the
 * blockers drop BELOW AntD's popup layer instead, and an open popup is wholly
 * usable while the page behind it stays inert. That is what lets a desktop step
 * say "open the box and choose CID REALTORS" as one instruction where the phone
 * needs two.
 *
 * WITH ONE EXCEPTION, and it is the read-only steps. An explainer draws a solid
 * scrim and cuts NO hole — its whole job is that the control it is pointing at
 * cannot be operated yet ("read it and carry on"). Under AntD's popup layer that
 * scrim misses anything inside an open Modal or Drawer, so the stage options an
 * explainer is describing stayed clickable and a stray click took the lesson
 * somewhere the next step could not follow. Those steps seal the layer back over
 * everything (`.is-sealed`); the ring and the coach bar still draw above it.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTour } from './TourContext';
import { getState } from './fixtures';

const PAD = 6;      // breathing room around the spotlit control
const BAR_GAP = 12; // between the bar and the target
// The coach bar's width on desktop. The phone's bar fills the app frame; a
// 1600px window would give a card of prose a 90-character measure, which reads
// as a banner rather than as someone talking you through the screen.
const BAR_W = 400;
// The DEMO strip's height (design.css --learn-ribbon-h). The phone never needs
// it here: its frame already STARTS below the strip, so measuring the frame
// keeps the bar clear of it. On desktop the frame is the whole window, so the
// bar has to be told where the strip ends or it parks on top of it.
const RIBBON_H = 34;
// How many consecutive frames the anchor must look covered before the step gives
// up on it. See `coveredFor` — one or two frames is the overlay arguing with
// itself, not a dialog in the way.
const COVERED_FRAMES = 6;

/**
 * Widen a control's rect to take in the LABEL that names it.
 *
 * A spotlight drawn tightly around an input leaves the agent looking at a bare
 * box: the coach bar flips above the anchor whenever there is no room below, and
 * the thing it lands on is precisely the label. "Last box — anything else the two
 * sides agreed" over an unnamed rectangle is a guess, not an instruction.
 *
 * Only a genuine single-field wrapper qualifies — a direct-child <label> and
 * exactly ONE control inside. That excludes the cards a step may also anchor
 * (the deposit card holds six inputs), which must keep their own bounds.
 */
function withLabel(el, r) {
  let node = el.parentElement;
  for (let i = 0; i < 4 && node; i += 1, node = node.parentElement) {
    // AntD nests the label one level deeper than a hand-rolled field does
    // (`.ant-form-item > .ant-form-item-label > label`), so a direct-child scan
    // alone finds nothing on the desktop build and every field there would be
    // spotlit as a bare box.
    const label = [...node.children].find((c) => c.tagName === 'LABEL')
      || (node.classList.contains('ant-form-item') ? node.querySelector(':scope > .ant-form-item-label label') : null);
    const controls = node.querySelectorAll('input, textarea, select, [role="button"], [role="listbox"]');
    if (label && controls.length <= 1) {
      const lr = label.getBoundingClientRect();
      if (lr.height > 0) {
        const top = Math.min(r.top, lr.top);
        const left = Math.min(r.left, lr.left);
        return {
          top,
          left,
          width: Math.max(r.left + r.width, lr.left + lr.width) - left,
          height: Math.max(r.top + r.height, lr.top + lr.height) - top,
        };
      }
    }
    // A wrapper holding more than one control is a card, not a field. Stop rather
    // than climbing past it and swallowing the whole section.
    if (controls.length > 1) break;
  }
  return r;
}

/**
 * What the agent has actually put into a spotlit control.
 *
 * A step's anchor is not always the input itself. On the phone it usually is —
 * the field renderers put `data-tour` straight on the <input> — but AntD wraps
 * its controls (InputNumber, Select, Form.Item), so on desktop the anchor is
 * routinely the wrapper. Reading `el.value` alone therefore reported "empty" for
 * a field the agent had just typed into, and the step would never let them past.
 *
 * Three places to look: the element itself, the option a Select is showing, and
 * an input inside the wrapper. A SELECT IS CHECKED BEFORE ITS INPUT — the input
 * inside one holds the SEARCH text, so reading that first would let a step past
 * on a half-typed query with nothing actually chosen.
 */
function filledValue(el) {
  if (!el) return '';
  if (typeof el.value === 'string' || typeof el.value === 'number') return String(el.value);
  const picked = el.querySelector('.ant-select-selection-item');
  if (picked) return picked.getAttribute('title') || picked.textContent || '';
  const inner = el.querySelector('input, textarea');
  return inner ? String(inner.value ?? '') : '';
}

/** Measure the anchor, and keep measuring while the page settles. */
function useAnchorRect(key, deps) {
  const [rect, setRect] = useState(null);
  const raf = useRef(0);
  /**
   * Consecutive frames the anchor has looked covered.
   *
   * The coach bar is placed FROM the rect, and when there is no rect it falls to
   * the bottom of the screen — where a page's own footer button lives. So losing
   * the rect moves the bar onto the anchor, and finding it moves the bar away
   * again: the two chase each other every frame, the ring strobes, and the bar
   * flickers between "tap the highlighted control" and a Next button.
   *
   * A real obstruction — a dialog, a sheet — stays for as long as it is open. A
   * feedback artefact lasts one frame. Requiring several in a row tells them
   * apart without slowing the honest case down perceptibly.
   */
  const coveredFor = useRef(0);

  const measure = useCallback(() => {
    if (!key) { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${key}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    // A zero box means it is mounted but not laid out yet (or hidden) — treat it
    // as absent so the step falls back rather than spotlighting the origin.
    if (r.width < 1 || r.height < 1) { setRect(null); return; }
    // And it must actually be REACHABLE. The blockers leave a gap over the anchor,
    // so whatever sits at its centre is what a finger would hit — if that is not
    // the anchor, something is covering it (a dialog the anchor's own tap opened,
    // most often) and spotlighting it would be a dead end. Treat it as absent so
    // the step fails soft to the skip escape instead.
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    if (cx >= 0 && cy >= 0 && cx <= window.innerWidth && cy <= window.innerHeight) {
      const hit = document.elementFromPoint(cx, cy);
      // Our OWN chrome never counts as covering the anchor. The blockers are
      // positioned from this very rect, so treating them as an obstruction
      // latches: rect goes null, the fallback paints one full-screen blocker,
      // and every later hit-test then finds that blocker and stays null forever.
      const ours = hit && hit.closest && hit.closest('.learn-chrome');
      // Our OWN chrome is EVIDENCE OF NOTHING — neither counted as an obstruction
      // nor as proof the anchor is clear. It has to be neutral in both directions:
      // counting it latches the rect off forever, and clearing the count on it
      // resurrects a rect we have already given up, which drops the bar onto the
      // anchor, which makes the next frame "ours" again — a strobing ring over a
      // document viewer the agent is trying to read.
      if (!ours) {
        const covered = hit && hit !== el && !el.contains(hit) && !hit.contains(el);
        coveredFor.current = covered ? coveredFor.current + 1 : 0;
      }
      if (coveredFor.current >= COVERED_FRAMES) { setRect(null); return; }
    }
    // Painted bounds, not measured bounds. The hit-test above deliberately used
    // the CONTROL's own centre — widening first would aim it at the label and the
    // step would read as covered — so the label is folded in only now.
    const b = withLabel(el, r);
    setRect({ top: b.top, left: b.left, width: b.width, height: b.height });
  }, [key]);

  useLayoutEffect(() => {
    // A new step, a new question. Carrying the count over latches the next step
    // off before it has been looked at even once — and because our own bar is
    // neutral in the hit-test, nothing would ever clear it again.
    coveredFor.current = 0;
    measure();
    // An anchor below the fold would be spotlit off-screen, so bring it into view
    // once, as soon as it exists. Without this the agent sees a dimmed page and a
    // bar telling them to tap something they cannot see.
    let scrolled = false;
    // Pages arrive asynchronously — a list renders, then its data lands and the
    // FAB moves. Re-measure for a beat rather than trusting the first frame.
    const tick = () => {
      measure();
      if (!scrolled && key) {
        const el = document.querySelector(`[data-tour="${key}"]`);
        if (el) {
          const r = el.getBoundingClientRect();
          // Horizontal matters as much as vertical: the sale's tab rail scrolls
          // sideways and "Invoices" is the 5th of 7 chips, so it starts off-screen
          // — and the blockers stop the agent scrolling to it themselves. Without
          // `inline` the step spotlights something they can neither see nor reach.
          //
          // Measured against the APP FRAME, not the window. The phone app is a
          // centred 460px column with `overflow-x: hidden`, so on a desktop
          // browser a chip sitting past the frame's right edge is clipped while
          // still being well inside the window — judged visible, spotlighted,
          // and untappable. The desktop build has no frame: the window IS the
          // bounds, and the bar is a card beside the anchor rather than a strip
          // across the bottom, so it needs far less room reserved under it.
          const fr = document.querySelector('.app-frame')?.getBoundingClientRect();
          const left = fr ? fr.left : 0;
          const right = fr ? fr.right : window.innerWidth;
          const foot = fr ? 220 : 40;
          const offV = r.top < 72 || r.bottom > window.innerHeight - foot;
          const offH = r.left < left + 8 || r.right > right - 8;
          if (r.height > 0 && (offV || offH)) {
            el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
          }
          scrolled = true;
        }
      }
      // No frame budget: an anchor can vanish mid-step (a sheet closing to open a
      // confirm), and a stale rect leaves the blockers cut around a control that is
      // no longer there — with the real dialog underneath them, unclickable.
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [measure, key, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const on = () => measure();
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [measure]);

  return rect;
}

/**
 * The box the chrome lives in.
 *
 * On the phone that is the app's own centred 460px column — a ribbon or a coach
 * bar spanning the whole browser window on a wide monitor would be floating in
 * the grey field beside the app rather than on it. On desktop the app IS the
 * window, so the window is the frame.
 */
function useFrameRect(desktop) {
  const [r, setR] = useState(null);
  useEffect(() => {
    const measure = () => {
      const el = desktop ? null : document.querySelector('.app-frame');
      const b = el ? el.getBoundingClientRect() : { left: 0, width: window.innerWidth, top: 0, height: window.innerHeight };
      setR({ left: b.left, width: b.width, top: b.top, height: b.height });
    };
    measure();
    window.addEventListener('resize', measure);
    const t = setInterval(measure, 500);
    return () => { window.removeEventListener('resize', measure); clearInterval(t); };
  }, [desktop]);
  return r;
}

const SKIN = { HQ: 'is-hq', PEER: 'is-peer' };
const ACTOR_TAG = { HQ: 'HQ', PEER: 'ANOTHER REN' };

export default function TourOverlay() {
  const tour = useTour();
  const loc = useLocation();
  const { active, step, index, total, played, play: runBeat, next, back, stop, choose } = tour || {};
  const [toast, setToast] = useState(null);
  // How long the anchor has been missing. A step's control routinely vanishes for
  // a moment mid-action — a sheet closing to open its confirm — and offering
  // "can't see it, skip" in that window tells the agent they are stuck at exactly
  // the moment they are not.
  const [lostFor, setLostFor] = useState(0);
  // The simulated actor working through their side of the job. Held here rather
  // than flipped instantly: the whole point of these steps is that work happens
  // somewhere the agent cannot see, and a status that changes the moment you tap
  // reads as nothing having happened at all.
  const [working, setWorking] = useState(null);
  // The bar's real height. Guessing it is what put the bar adrift in the middle
  // of the page when the anchor was a sticky footer.
  const barRef = useRef(null);
  const [barH, setBarH] = useState(0);
  useLayoutEffect(() => {
    if (!barRef.current) return;
    const h = barRef.current.getBoundingClientRect().height;
    if (h && Math.abs(h - barH) > 1) setBarH(h);
  });

  const desktop = !!tour?.desktop;
  const rect = useAnchorRect(active ? step?.anchor : null, [loc.pathname, index]);
  const frame = useFrameRect(desktop);

  const isSim = step?.actor === 'HQ' || step?.actor === 'PEER';
  const needsPlay = isSim && !played;

  /* Advance when the agent actually operates the spotlit control. Capture phase,
     so it still counts if the control stops propagation itself. */
  useEffect(() => {
    if (!active || !step?.anchor || !step?.advanceOnTap || needsPlay) return undefined;
    const onClick = (e) => {
      const el = document.querySelector(`[data-tour="${step.anchor}"]`);
      if (el && (el === e.target || el.contains(e.target))) {
        // Let the app handle the tap first; advance on the next tick so the page
        // it navigates to is the one the next step measures against.
        setTimeout(() => next(), 60);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [active, step, needsPlay, next]);

  /* A step may name the fields the FORM itself requires. Until they hold a value
     the step offers no way forward — the walkthrough must never let an agent past
     a required field the real form would reject, or it teaches a shortcut that
     does not exist. Re-checked on a timer because typing does not re-render us. */
  const [gateOk, setGateOk] = useState(true);
  useEffect(() => {
    const keys = step?.requireFilled;
    if (!active || !keys || !keys.length) { setGateOk(true); return undefined; }
    // A step must point at the field it is asking for. Demanding one the
    // spotlight is not on leaves the agent filling the highlighted box and
    // finding no way forward, with the coach bar sitting over the box that is
    // actually blocking them — exactly the dead end this guard exists to catch.
    if (import.meta.env.DEV) {
      // The rule is reach, not arithmetic: every field the step demands has to be
      // inside the spotlight. One field per step satisfies that trivially; so does
      // anchoring a CARD that contains all six of them, which is the honest way to
      // ask for one decision spread over several boxes.
      const host = step.anchor && document.querySelector(`[data-tour="${step.anchor}"]`);
      const outside = keys.filter((k) => {
        if (k === step.anchor) return false;
        const el = document.querySelector(`[data-tour="${k}"]`);
        return !el || !host || !host.contains(el);
      });
      if (outside.length) {
        // eslint-disable-next-line no-console
        console.warn(`[learn] step "${step.id}" requires ${outside.join(', ')}, which "${step.anchor}" does not contain. The agent cannot reach them — split the step, or anchor the card that holds them all.`);
      }
    }
    const check = () => setGateOk(keys.every((k) => {
      const el = document.querySelector(`[data-tour="${k}"]`);
      return !!el && filledValue(el).trim() !== '';
    }));
    check();
    const t = setInterval(check, 250);
    return () => clearInterval(t);
  }, [active, step]);

  /* Some outcomes live in a page's own React state, not in the sandbox — whether
     a party has been picked onto the booking form, for one. Those steps test the
     DOM instead, which is the only place that state is observable from here. */
  useEffect(() => {
    if (!active || !step?.advanceWhenDom) return undefined;
    let armed = false;
    try { armed = !step.advanceWhenDom(); } catch { armed = true; }
    const t = setInterval(() => {
      try {
        const ok = step.advanceWhenDom();
        if (!ok) armed = true;
        else if (armed) next();
      } catch { /* mid-render */ }
    }, 300);
    return () => clearInterval(t);
  }, [active, step, next]);

  /* The strongest completion test: wait for the sandbox to actually reach the
     state the step asked for. A tap is not the outcome — tapping "Change" only
     OPENS the stage sheet, and advancing there is what made the tour skip the
     lesson it existed to teach. */
  useEffect(() => {
    if (!active || !step?.advanceWhen) return undefined;
    // Only a FALSE -> TRUE transition advances. A gate that is already satisfied
    // when the step opens means the agent got here by going Back over work they
    // already did — auto-advancing there would bounce them straight forward again
    // and make the step unreachable.
    let armed = !step.advanceWhen(getState());
    const t = setInterval(() => {
      const ok = step.advanceWhen(getState());
      if (!ok) armed = true;
      else if (armed) next();
    }, 250);
    return () => clearInterval(t);
  }, [active, step, next]);

  /* Some steps complete by arriving somewhere rather than by tapping. */
  useEffect(() => {
    if (!active || !step?.advanceOnRoute) return;
    if (loc.pathname === step.advanceOnRoute) next();
  }, [active, step, loc.pathname, next]);

  useEffect(() => { setToast(null); setWorking(null); }, [index]);

  useEffect(() => {
    setLostFor(0);
    if (rect) return undefined;
    const t = setInterval(() => setLostFor((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [rect, index]);

  const play = () => {
    const lines = (step?.hqSteps && step.hqSteps.length)
      ? step.hqSteps
      : ['Opening your file', 'Checking the figures', 'Updating your deal'];
    setWorking({ i: 0, lines });
    let i = 0;
    const tick = setInterval(() => {
      i += 1;
      if (i < lines.length) { setWorking({ i, lines }); return; }
      clearInterval(tick);
      // Everything lands together at the end: the record changes, the page
      // re-reads it, and the notification arrives — the way it would in real life.
      setWorking(null);
      runBeat();
      if (step?.toast) {
        setToast(step.toast);
        setTimeout(() => setToast(null), 4600);
      }
    }, 850);
  };

  if (!active || !step || !frame) return null;

  /* What the bar has to keep clear of.
     Normally the spotlit control. But a control that OPENS something — a Select,
     a date picker — is only half of what the step is about: the list it drops
     below itself is the half the agent has to reach, and a bar placed against
     the trigger alone lands squarely on it. On desktop the tour deliberately
     lets those popups be used in place (see the blocker layer), so it has to
     make room for them too. Phone dropdowns are the app's own bottom sheets and
     are handled by anchoring a row inside them, so this is desktop-only. */
  const avoid = (() => {
    if (!rect || !desktop) return rect;
    const open = [...document.querySelectorAll('.ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown')]
      .filter((d) => !d.classList.contains('ant-select-dropdown-hidden') && !d.classList.contains('ant-slide-up-leave'));
    let r = rect;
    for (const d of open) {
      const pr = d.getBoundingClientRect();
      if (pr.width < 1 || pr.height < 1) continue;
      // Only the popup this control spawned: it is positioned from the trigger,
      // so it shares its column. One elsewhere on the page is not our business.
      if (pr.right < rect.left - 4 || pr.left > rect.right + 4) continue;
      const top = Math.min(r.top, pr.top);
      const left = Math.min(r.left, pr.left);
      r = {
        top,
        left,
        width: Math.max(r.left + r.width, pr.left + pr.width) - left,
        height: Math.max(r.top + r.height, pr.top + pr.height) - top,
      };
    }
    return r;
  })();

  // Sit the bar just under the target, or just above it when there is no room
  // below. Measured, not guessed: a hardcoded height left the bar floating in
  // the middle of the page whenever the anchor was a sticky footer.
  const h = barH || 168;
  const below = avoid ? avoid.top + avoid.height + BAR_GAP : 0;
  const fitsBelow = avoid ? below + h < window.innerHeight - 8 : false;
  const barAtTop = !!avoid && !fitsBelow;

  /* Horizontally: the phone bar fills the frame, because the frame is barely
     wider than the bar. Desktop tracks the anchor instead — a card of prose
     pinned to the left of a 1600px window while the ring breathes away on the
     right is two conversations, not one — and is clamped so it can never hang
     off either edge. */
  const barW = desktop ? Math.min(BAR_W, frame.width - 32) : frame.width - 20;

  /* …unless the step's control lives inside a DRAWER, in which case the bar goes
     BESIDE the drawer rather than over it.

     A drawer here is a form — quick-create a party, record a receipt — and the
     flip-above-or-below rule that serves a phone screen puts the bar squarely on
     the next three fields, because a 560px panel has no room above or below the
     field being described. The empty page next to it has room for both. Only
     drawers: a Modal is centred and short, and pushing the bar out to the margin
     of the window would separate it from what it is talking about. */
  const panelDock = (() => {
    if (!desktop || !rect || !step.anchor) return null;
    const el = document.querySelector(`[data-tour="${step.anchor}"]`);
    const panel = el && el.closest && el.closest('.ant-drawer-content-wrapper');
    if (!panel) return null;
    const pr = panel.getBoundingClientRect();
    if (pr.left >= barW + 32) return pr.left - barW - 16;
    if (window.innerWidth - pr.right >= barW + 32) return pr.right + 16;
    return null;
  })();

  const barLeft = desktop
    ? Math.max(16, Math.min(
      frame.width - barW - 16,
      panelDock ?? (rect ? rect.left + rect.width / 2 - barW / 2 : (frame.width - barW) / 2),
    ))
    : frame.left + 10;

  // Never over the DEMO strip: it is the one piece of chrome that says none of
  // this is real, and a bar sitting on it hides exactly that.
  const barTopMin = desktop ? RIBBON_H + 8 : 8;

  const barStyle = {
    left: barLeft,
    width: barW,
    ...(rect
      ? {
        top: panelDock !== null
          // Docked: level with the control, not under it.
          ? Math.min(Math.max(barTopMin, rect.top + rect.height / 2 - h / 2), window.innerHeight - h - 8)
          : Math.max(barTopMin, barAtTop ? avoid.top - BAR_GAP - h : below),
      }
      // No anchor: pin the bar to the TOP, never the bottom. Sticky footers, sheet
      // confirm buttons and picker rows all live at the bottom of a phone screen,
      // so a bar parked there covers the very control the agent is reaching for —
      // and since our own chrome is neutral in the hit-test, the tour cannot see
      // that it is the thing in the way.
      : { top: Math.max(barTopMin, (frame?.top || 0) + 8) }),
  };

  /* Which way the "do it to carry on" hint points. Docked beside a drawer, the
     control is sideways from the bar, and an arrow that says "up" when the thing
     is to the right sends the agent looking in the wrong place. */
  const arrow = panelDock !== null
    ? (rect && panelDock < rect.left ? '→' : '←')
    : (barAtTop ? '↓' : '↑');

  // A step that completes on an outcome rather than a tap.
  const isGated = !!(step.advanceWhen || step.advanceWhenDom || step.requireFilled);

  /**
   * An EXPLAINER is any step whose only way forward is the Next button: it asks
   * for no tap and waits on no outcome. The agent therefore has no reason to
   * operate the thing it is pointing at — and every reason not to, since these
   * steps point at cards that contain live controls (the stage card holds
   * "Change", the ready-to-issue card holds "Issue form"). Leaving the hole open
   * invites a tap that takes the lesson somewhere the next step cannot follow, so
   * explainers are read-only by default. `interactive: true` opts back in.
   */
  const readOnly = step.readOnly ?? (!step.advanceOnTap && !isGated && !step.interactive);

  /**
   * A tappable step must not point at a control that OPENS AN OVERLAY the tour
   * cannot follow into. A SheetSelect trigger renders its picker at z-index 4900
   * with option rows outside the spotlight's hole, so the agent can neither
   * choose nor dismiss it — the tour simply stops. If a step genuinely needs the
   * picker, give the NEXT step an anchor inside it.
   *
   * PHONE ONLY. The desktop build puts its blockers under AntD's popup layer
   * precisely so a step CAN say "open this and choose that" — warning there
   * would be telling the author off for using the thing built for them.
   */
  if (import.meta.env.DEV && !desktop && !readOnly && !needsPlay) {
    const el = step.anchor && document.querySelector(`[data-tour="${step.anchor}"]`);
    if (el?.getAttribute('aria-haspopup') === 'listbox' && !step.advanceOnTap) {
      // eslint-disable-next-line no-console
      console.warn(`[learn] step "${step.id}" makes a picker tappable but nothing follows it into the sheet. Make it read-only, or anchor the next step inside the picker.`);
    }
  }

  const pct = Math.round(((index + 1) / total) * 100);

  /* THE SCRIM AND THE RING ARE TWO LAYERS, and on desktop they are not adjacent.
     The ring, the ribbon and the coach bar must sit above everything the app can
     raise; the blockers must sit above the PAGE but, on desktop, below AntD's
     popup layer, so a Drawer or a Select dropdown the step sent the agent into is
     wholly usable. One element cannot be in two stacking contexts, so they are
     portalled separately and design.css gives each its z-index per build.
     `learn-chrome` is on both, because the anchor hit-test has to recognise our
     own furniture wherever it lives — see useAnchorRect. */
  const spotlight = rect && !needsPlay && !readOnly ? (
    /* Four blockers around the target, not one transparent sheet. Everything
       except the spotlit control is unclickable, which does two jobs at once: it
       keeps the step unambiguous, and it means the agent cannot wander into a
       photo upload or the Copilot stream — the paths a demo adapter on the axios
       instances cannot fully cover. */
    <>
      <div className="learn-block" style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - PAD) }} />
      <div className="learn-block" style={{ top: rect.top + rect.height + PAD, left: 0, right: 0, bottom: 0 }} />
      <div className="learn-block" style={{ top: rect.top - PAD, left: 0, width: Math.max(0, rect.left - PAD), height: rect.height + PAD * 2 }} />
      <div className="learn-block" style={{ top: rect.top - PAD, left: rect.left + rect.width + PAD, right: 0, height: rect.height + PAD * 2 }} />
    </>
  ) : readOnly && rect ? (
    /* An explainer step: SHOW the control, but keep it unfireable. These steps
       describe an option the agent must not pick yet — leaving the hole open
       would invite the tap the copy is telling them not to make, and firing it
       takes the lesson somewhere the next step cannot follow. */
    <div className="learn-block" style={{ inset: 0 }} />
  ) : (
    /* No anchor to focus on — dim, but never block. The step's control has
       usually just moved (a sheet closing to open its confirm dialog), and a
       blocking scrim here puts the overlay on top of the very button the agent
       has to press next. */
    <div className="learn-dim-all" />
  );

  const ring = rect && !needsPlay ? (
    <div
      className={`learn-ring${readOnly ? ' is-readonly' : ''}`}
      style={{
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }}
    />
  ) : null;

  // Portalled to <body>, NOT rendered inside #root. Reserving the ribbon's strip
  // gives #root a transform, which would make it the containing block for this
  // overlay's own `position: fixed` children — every blocker would then be offset
  // by the ribbon's height while the anchor rects stay in viewport coordinates,
  // and the spotlight would sit next to the control instead of on it.
  return (
    <>
      {/* `is-sealed` when the step is an explainer: see the layer note above. */}
      {createPortal(
        <div className={`learn-chrome learn-blocklayer${readOnly && rect ? ' is-sealed' : ''}`}>{spotlight}</div>,
        document.body,
      )}
      {createPortal(
    <div className="learn-chrome learn-overlay" aria-live="polite">
      {/* DEMO ribbon — a pure marker, with NO controls of its own.
          It shares the top strip with any full-screen overlay the app opens (the
          file viewer puts Share and Close exactly there), so an Exit button here
          sat on top of them and ate the tap. The coach bar already carries Exit. */}
      <div className="learn-ribbon-fixed" style={{ left: frame.left, width: frame.width, top: 0 }}>
        <span>● DEMO — pretend deal · practice only</span>
      </div>

      {ring}

      {/* The phone lands this as a banner across the app; on a wide window the
          same thing spanning 1600px reads as a page header rather than as a
          notification arriving, so it takes the corner one would arrive in. */}
      {toast && (
        <div
          className="learn-toast"
          style={desktop
            ? { left: frame.width - 372, width: 356, top: frame.top + 46 }
            : { left: frame.left + 12, width: frame.width - 24, top: frame.top + 46 }}
        >
          <div className="ic" style={{ background: `color-mix(in srgb,var(--${toast.tone === 'warn' ? 'warn' : 'ok'}) 16%,transparent)`, color: `var(--${toast.tone === 'warn' ? 'warn' : 'ok'})` }}>
            {toast.tone === 'warn' ? '◈' : '✓'}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="tt">{toast.title}</div>
            <div className="bb">{toast.body}</div>
          </div>
        </div>
      )}

      <div ref={barRef} className={`learn-coach is-fixed ${SKIN[step.actor] || ''}`} style={barStyle}>
        <div className="top">
          {isSim && <span className="hqtag">{ACTOR_TAG[step.actor]}</span>}
          <span>{step.chapter} · {index + 1} OF {total}</span>
          <button type="button" className="sk" onClick={stop}>Exit</button>
        </div>
        <div className="trk"><i style={{ width: `${pct}%` }} /></div>
        {working ? (
          <div className="learn-working">
            <div className="hd">
              {step.actor === 'HQ' ? 'Head office is working on it' : 'They are working on it'}
              <span className="dots"><i /><i /><i /></span>
            </div>
            {working.lines.map((line, k) => (
              <div key={line} className={`ln${k < working.i ? ' done' : ''}${k === working.i ? ' now' : ''}`}>
                <span className="mk">{k < working.i ? '✓' : k === working.i ? '' : '·'}</span>
                {line}
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="say">{typeof step.say === 'function' ? step.say() : step.say}</div>
            {step.do && <div className="do">{typeof step.do === 'function' ? step.do() : step.do}</div>}
          </>
        )}
        {!working && step.choices && (
          /* The fork. Two ways to reach the same form, and the agent picks which
             one this run teaches — offered as the step's only way forward, so the
             lesson cannot slide past the choice on a stray Next. */
          <div className="picks">
            {step.choices.map((c) => (
              <button type="button" key={c.key} className="pk" onClick={() => choose(c.key)}>
                <span className="pl">{c.label}</span>
                <span className="ph">{c.hint}</span>
              </button>
            ))}
          </div>
        )}
        {!working && (
        <div className="acts">
          {index > 0 && <button type="button" className="gh" onClick={back}>Back</button>}
          {step.choices ? (
            <span className="ptr">pick one to carry on</span>
          ) : needsPlay ? (
            <button type="button" className="nx" onClick={play}>
              ▶ Watch {step.actor === 'HQ' ? 'HQ' : 'them'} do it
            </button>
          ) : !gateOk ? (
            <span className="ptr">{arrow} {step.gateHint || 'fill this in to carry on'}</span>
          ) : (step.advanceOnTap || step.advanceWhen || step.advanceWhenDom) && rect ? (
            /* A do step carries no Next — the highlighted control is the only way
               forward, which is the whole point of the spotlight. An advanceWhen
               step keeps that promise harder: it waits for the deed, not the tap,
               so an agent cannot skip past the thing the step exists to teach. */
            <span className="ptr">
              {arrow} {step.advanceWhen || step.advanceWhenDom
                ? 'do it to carry on'
                : `${desktop ? 'click' : 'tap'} the highlighted control`}
            </span>
          ) : isGated && !rect ? (
            <span className="ptr">carry on — this moves by itself</span>
          ) : (
            <button type="button" className="nx" onClick={next}>
              {index + 1 === total ? 'Finish ✓' : 'Next'}
            </button>
          )}
        </div>
        )}
        {/* A missing anchor must never trap the agent on a dead step. */}
        {!rect && !needsPlay && lostFor >= 6 && (step.advanceOnTap || step.advanceWhen || step.advanceWhenDom || step.requireFilled) && (
          <button type="button" className="gh" style={{ marginTop: 4 }} onClick={next}>
            Can’t see it — skip this step
          </button>
        )}
      </div>
    </div>,
        document.body,
      )}
    </>
  );
}
